import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { completeTrip, getTrips } from '../services/api';
import { compressImage } from '../utils/imageCompress';

const MAX_PHOTO_AGE_MS = 15 * 60 * 1000;

export default function TripReturn() {
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState('');
  const [inTime, setInTime] = useState('');
  const [meterIn, setMeterIn] = useState('');
  const [driverStatement, setDriverStatement] = useState('');
  const [meterPhoto, setMeterPhoto] = useState('');
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    getTrips({ status: 'pending' })
      .then((rows) => setTrips(Array.isArray(rows) ? rows : []))
      .catch(() => setTrips([]));
  }, []);

  const selected = trips.find((t) => String(t._id) === String(tripId));

  const openCamera = () => {
    if (photoInputRef.current) photoInputRef.current.click();
  };

  const onPhotoSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.lastModified && Date.now() - file.lastModified > MAX_PHOTO_AGE_MS) {
      toast.error('This photo is too old. Take a new photo of the meter now.');
      return;
    }
    setProcessingPhoto(true);
    try {
      setMeterPhoto(await compressImage(file));
    } catch (err) {
      toast.error(err.message || 'Could not use this photo');
    } finally {
      setProcessingPhoto(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!tripId) return;
    if (!meterPhoto) {
      toast.error('Take a photo of the meter before completing the trip.');
      return;
    }
    setSubmitting(true);
    try {
      await completeTrip(tripId, { inTime, meterIn: Number(meterIn), driverStatement, meterPhoto });
      toast.success('Trip completed');
      setTripId('');
      setInTime('');
      setMeterIn('');
      setDriverStatement('');
      setMeterPhoto('');
      const rows = await getTrips({ status: 'pending' });
      setTrips(Array.isArray(rows) ? rows : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to complete trip');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2>Vehicle In</h2>
      <form className="card" onSubmit={onSubmit}>
        <div className="form-group">
          <label>Active trip</label>
          <select value={tripId} onChange={(e) => setTripId(e.target.value)} required>
            <option value="">Select pending trip</option>
            {trips.map((t) => (
              <option key={t._id} value={t._id}>
                {t.vehicle?.name || 'Vehicle'} — {t.driver?.name || 'Driver'} ({t.outTime || 'out'})
              </option>
            ))}
          </select>
        </div>
        {selected && (
          <p className="text-muted">Meter out: {selected.meterOut} · Expected km: {selected.expectedKM ?? '—'}</p>
        )}
        <div className="form-grid cols2">
          <div className="form-group"><label>In time</label><input value={inTime} onChange={(e) => setInTime(e.target.value)} required /></div>
          <div className="form-group"><label>Meter in</label><input type="number" value={meterIn} onChange={(e) => setMeterIn(e.target.value)} required /></div>
        </div>

        <div className="form-group">
          <label>Meter photo (required)</label>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPhotoSelected}
            style={{ display: 'none' }}
          />
          {meterPhoto ? (
            <div className="meter-photo-preview">
              <img src={meterPhoto} alt="Meter" />
              <button type="button" className="btn btn-sm" onClick={openCamera} disabled={processingPhoto || submitting}>
                Retake
              </button>
            </div>
          ) : (
            <button type="button" className="btn meter-photo-capture" onClick={openCamera} disabled={processingPhoto}>
              {processingPhoto ? 'Processing photo…' : 'Take meter photo'}
            </button>
          )}
          <p className="text-muted text-sm mt8">
            Hold the phone close so the odometer digits fill the frame, avoid glare.
          </p>
        </div>

        <div className="form-group">
          <label>Driver statement (required if suspicious)</label>
          <textarea rows={3} value={driverStatement} onChange={(e) => setDriverStatement(e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={submitting || processingPhoto || !meterPhoto}>
          {submitting ? 'Saving…' : 'Complete trip'}
        </button>
      </form>
    </div>
  );
}
